import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { 
  ArrowDownUp, 
  Wallet, 
  Settings, 
  X, 
  CheckCircle, 
  AlertTriangle,
  Loader,
  ExternalLink,
  TrendingUp,
  DollarSign,
  Zap,
  Info,
  RefreshCw,
  ChevronDown,
  Shield,
  Moon
} from 'lucide-react';
import './Swap.css';

// Contract Addresses (Polygon Mainnet)
// CRITICAL: These must match the addresses in TokenClaim.jsx
const TPH_ADDRESS = process.env.REACT_APP_TPHILE_TOKEN_CONTRACT || '0x9A77152343981490c6477aF75A7c3FCA28cf9f00'; // TPHILE Token
const USDC_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'; // USDC Token on Polygon Mainnet
const ROUTER_ADDRESS = '0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff'; // QuickSwap Router on Polygon Mainnet
const PAIR_ADDRESS = ''; // TPH/USDC Pair (to be determined after liquidity is added)

const TARGET_CHAIN_ID = 137; // Polygon Mainnet
const CHAIN_NAME = 'Polygon Mainnet';
const EXPLORER_URL = 'https://polygonscan.com';

// ERC20 ABI
const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)'
];

// Uniswap V2 Router ABI
const ROUTER_ABI = [
  'function getAmountsOut(uint amountIn, address[] memory path) public view returns (uint[] memory amounts)',
  'function swapExactTokensForTokens(uint amountIn, uint amountOutMin, address[] calldata path, address to, uint deadline) external returns (uint[] memory amounts)',
  'function factory() external view returns (address)'
];

// Factory ABI
const FACTORY_ABI = [
  'function getPair(address tokenA, address tokenB) external view returns (address pair)'
];

// Pair ABI for reserves
const PAIR_ABI = [
  'function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
  'function token0() external view returns (address)',
  'function token1() external view returns (address)'
];

const Swap = () => {
  // Wallet State
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  // Token Balances
  const [tphBalance, setTphBalance] = useState('0');
  const [usdcBalance, setUsdcBalance] = useState('0');

  // Swap State
  const [fromToken, setFromToken] = useState('TPH');
  const [toToken, setToToken] = useState('USDC');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState('0');
  const [priceImpact, setPriceImpact] = useState('0');
  const [slippage, setSlippage] = useState('0.5');
  const [deadline, setDeadline] = useState('20');

  // UI State
  const [showSettings, setShowSettings] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStep, setSwapStep] = useState('');
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Pool Data
  const [poolData, setPoolData] = useState({
    tphReserve: '0',
    usdcReserve: '0',
    loading: true
  });

  // Diagnostic State
  const [diagnostics, setDiagnostics] = useState({
    routerExists: null,
    tphExists: null,
    usdcExists: null,
    pairExists: null,
    pairAddress: null,
    hasLiquidity: null
  });

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    return typeof window.ethereum !== 'undefined';
  };

  // 🔍 DIAGNOSTIC: Comprehensive Pre-Flight Checks
  const runDiagnostics = async (web3Provider) => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('🔍 [DIAGNOSTIC] Starting comprehensive system check...');
    console.log('═══════════════════════════════════════════════════════');
    
    const results = {
      routerExists: false,
      tphExists: false,
      usdcExists: false,
      pairExists: false,
      pairAddress: null,
      hasLiquidity: false
    };

    try {
      // Check network
      const network = await web3Provider.getNetwork();
      console.log('🌐 [DIAGNOSTIC] Network Info:');
      console.log('   ├─ Chain ID:', network.chainId.toString());
      console.log('   ├─ Chain Name:', network.name);
      console.log('   └─ Expected Chain ID:', TARGET_CHAIN_ID);
      
      if (Number(network.chainId) !== TARGET_CHAIN_ID) {
        console.log('⚠️ [DIAGNOSTIC] WRONG NETWORK! Expected:', TARGET_CHAIN_ID, 'Got:', network.chainId.toString());
      }

      // Check Router Contract
      console.log('\n📋 [DIAGNOSTIC] Checking Router Contract:');
      console.log('   └─ Address:', ROUTER_ADDRESS);
      try {
        const routerCode = await web3Provider.getCode(ROUTER_ADDRESS);
        results.routerExists = routerCode !== '0x';
        console.log('   └─ Contract Exists:', results.routerExists);
        
        if (results.routerExists) {
          const routerContract = new ethers.Contract(ROUTER_ADDRESS, ROUTER_ABI, web3Provider);
          const factoryAddress = await routerContract.factory();
          console.log('   └─ Factory Address:', factoryAddress);
        } else {
          console.error('   └─ ❌ NO CONTRACT CODE AT ROUTER ADDRESS!');
        }
      } catch (err) {
        console.error('   └─ ❌ Router check failed:', err.message);
      }

      // Check TPH Token Contract
      console.log('\n🪙 [DIAGNOSTIC] Checking TPH Token Contract:');
      console.log('   └─ Address:', TPH_ADDRESS);
      try {
        const tphCode = await web3Provider.getCode(TPH_ADDRESS);
        results.tphExists = tphCode !== '0x';
        console.log('   └─ Contract Exists:', results.tphExists);
        
        if (results.tphExists) {
          const tphContract = new ethers.Contract(TPH_ADDRESS, ERC20_ABI, web3Provider);
          const symbol = await tphContract.symbol();
          const decimals = await tphContract.decimals();
          console.log('   ├─ Symbol:', symbol);
          console.log('   └─ Decimals:', decimals);
        } else {
          console.error('   └─ ❌ NO CONTRACT CODE AT TPH ADDRESS!');
        }
      } catch (err) {
        console.error('   └─ ❌ TPH check failed:', err.message);
      }

      // Check USDC Token Contract
      console.log('\n💵 [DIAGNOSTIC] Checking USDC Token Contract:');
      console.log('   └─ Address:', USDC_ADDRESS);
      try {
        const usdcCode = await web3Provider.getCode(USDC_ADDRESS);
        results.usdcExists = usdcCode !== '0x';
        console.log('   └─ Contract Exists:', results.usdcExists);
        
        if (results.usdcExists) {
          const usdcContract = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, web3Provider);
          const symbol = await usdcContract.symbol();
          const decimals = await usdcContract.decimals();
          console.log('   ├─ Symbol:', symbol);
          console.log('   └─ Decimals:', decimals);
        } else {
          console.error('   └─ ❌ NO CONTRACT CODE AT USDC ADDRESS!');
        }
      } catch (err) {
        console.error('   └─ ❌ USDC check failed:', err.message);
      }

      // Check Liquidity Pool
      console.log('\n🏊 [DIAGNOSTIC] Checking Liquidity Pool:');
      try {
        if (results.routerExists) {
          const routerContract = new ethers.Contract(ROUTER_ADDRESS, ROUTER_ABI, web3Provider);
          const factoryAddress = await routerContract.factory();
          const factoryContract = new ethers.Contract(factoryAddress, FACTORY_ABI, web3Provider);
          const pairAddress = await factoryContract.getPair(TPH_ADDRESS, USDC_ADDRESS);
          
          results.pairAddress = pairAddress;
          results.pairExists = pairAddress !== '0x0000000000000000000000000000000000000000';
          
          console.log('   ├─ Factory:', factoryAddress);
          console.log('   ├─ Pair Address:', pairAddress);
          console.log('   └─ Pair Exists:', results.pairExists);
          
          if (results.pairExists) {
            const pairContract = new ethers.Contract(pairAddress, PAIR_ABI, web3Provider);
            const reserves = await pairContract.getReserves();
            console.log('   ├─ Reserve0:', ethers.formatUnits(reserves[0], 18));
            console.log('   ├─ Reserve1:', ethers.formatUnits(reserves[1], 6));
            results.hasLiquidity = reserves[0] > 0 && reserves[1] > 0;
            console.log('   └─ Has Liquidity:', results.hasLiquidity);
          } else {
            console.error('   └─ ❌ NO LIQUIDITY POOL EXISTS FOR TPH/USDC!');
          }
        }
      } catch (err) {
        console.error('   └─ ❌ Pool check failed:', err.message);
      }

      // Note: Router's getAmountsOut is not used - we calculate directly from pair reserves
      console.log('\n🧪 [DIAGNOSTIC] Calculation Method:');
      console.log('   └─ ✅ Using DIRECT PAIR calculation (bypasses router)');
      console.log('   └─ ℹ️ Router\'s getAmountsOut() is not needed');
      console.log('   └─ ℹ️ We use constant product formula directly');

      console.log('\n═══════════════════════════════════════════════════════');
      console.log('📊 [DIAGNOSTIC] Summary:');
      console.log('═══════════════════════════════════════════════════════');
      console.log('Router Contract:', results.routerExists ? '✅ EXISTS' : '❌ NOT FOUND');
      console.log('TPH Token:', results.tphExists ? '✅ EXISTS' : '❌ NOT FOUND');
      console.log('USDC Token:', results.usdcExists ? '✅ EXISTS' : '❌ NOT FOUND');
      console.log('Liquidity Pool:', results.pairExists ? '✅ EXISTS' : '❌ NOT FOUND');
      console.log('Pool Liquidity:', results.hasLiquidity ? '✅ HAS LIQUIDITY' : '❌ EMPTY OR N/A');
      console.log('═══════════════════════════════════════════════════════\n');

      setDiagnostics(results);
      return results;
    } catch (err) {
      console.error('❌ [DIAGNOSTIC] Critical error during diagnostics:', err);
      return results;
    }
  };

  // Auto-detect existing wallet connection
  const checkExistingConnection = async () => {
    console.log('🔍 [Swap] Checking for existing wallet connection...');
    
    if (!isMetaMaskInstalled()) {
      console.log('❌ [Swap] MetaMask not installed');
      setError('MetaMask is not installed. Please install MetaMask to use the swap feature.');
      setIsInitializing(false);
      return;
    }

    try {
      // Check if accounts are already connected (no popup)
      const accounts = await window.ethereum.request({ 
        method: 'eth_accounts' // This doesn't prompt user
      });
      
      console.log('🔍 [Swap] eth_accounts result:', accounts);
      
      if (accounts.length === 0) {
        console.log('⚠️ [Swap] No wallet connected');
        setError('Please connect your wallet from the My Account page (TPH tab) first');
        setIsInitializing(false);
        return;
      }

      console.log('✅ [Swap] Existing wallet detected:', accounts[0]);
      
      const web3Provider = new ethers.BrowserProvider(window.ethereum);
      const web3Signer = await web3Provider.getSigner();
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });

      console.log('🔗 [Swap] Chain ID:', parseInt(chainId, 16), '(Expected:', TARGET_CHAIN_ID, ')');

      setProvider(web3Provider);
      setSigner(web3Signer);
      setWalletAddress(accounts[0]);
      setIsConnected(true);

      // Check network
      if (parseInt(chainId, 16) !== TARGET_CHAIN_ID) {
        console.log('⚠️ [Swap] Wrong network detected');
        setIsWrongNetwork(true);
        setIsInitializing(false);
        await switchNetwork();
      } else {
        console.log('✅ [Swap] Correct network, fetching balances...');
        setIsWrongNetwork(false);
        
        // Run comprehensive diagnostics
        console.log('\n🔍 [Swap] Running system diagnostics...');
        const diagnosticResults = await runDiagnostics(web3Provider);
        
        await fetchBalances(accounts[0], web3Provider);
        await fetchPoolData(web3Provider);
        
        // Show diagnostic warnings if any issues found
        if (!diagnosticResults.routerExists) {
          setError('⚠️ Router contract not found at ' + ROUTER_ADDRESS);
        } else if (!diagnosticResults.tphExists) {
          setError('⚠️ TPH token contract not found at ' + TPH_ADDRESS);
        } else if (!diagnosticResults.usdcExists) {
          setError('⚠️ USDC token contract not found at ' + USDC_ADDRESS);
        } else if (!diagnosticResults.pairExists) {
          setError('⚠️ No liquidity pool exists for TPH/USDC pair. Please add liquidity on the DEX first.');
        } else if (!diagnosticResults.hasLiquidity) {
          setError('⚠️ Liquidity pool exists but has no liquidity. Please add liquidity first.');
        }
        
        setIsInitializing(false);
      }
    } catch (err) {
      console.error('❌ [Swap] Error checking wallet connection:', err);
      setError('Failed to detect wallet connection: ' + err.message);
      setIsInitializing(false);
    }
  };

  // Switch Network
  const switchNetwork = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${TARGET_CHAIN_ID.toString(16)}` }],
      });
      setIsWrongNetwork(false);
    } catch (err) {
      if (err.code === 4902) {
        // Network not added, try to add it
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${TARGET_CHAIN_ID.toString(16)}`,
              chainName: CHAIN_NAME,
              nativeCurrency: {
                name: 'POL',
                symbol: 'POL',
                decimals: 18
              },
              rpcUrls: ['https://polygon-rpc.com'],
              blockExplorerUrls: [EXPLORER_URL]
            }]
          });
          setIsWrongNetwork(false);
        } catch (addErr) {
          console.error('Error adding network:', addErr);
          setError('Failed to add network');
        }
      } else {
        console.error('Error switching network:', err);
        setError('Failed to switch network');
      }
    }
  };

  // Fetch Token Balances
  const fetchBalances = async (address, web3Provider) => {
    console.log('💰 [Swap] Fetching balances for:', address);
    console.log('💰 [Swap] TPH Contract:', TPH_ADDRESS);
    console.log('💰 [Swap] USDC Contract:', USDC_ADDRESS);
    
    try {
      const tphContract = new ethers.Contract(TPH_ADDRESS, ERC20_ABI, web3Provider);
      const usdcContract = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, web3Provider);

      const [tphBal, usdcBal] = await Promise.all([
        tphContract.balanceOf(address),
        usdcContract.balanceOf(address)
      ]);

      const tphFormatted = ethers.formatUnits(tphBal, 18);
      const usdcFormatted = ethers.formatUnits(usdcBal, 6);

      console.log('✅ [Swap] TPH Balance (raw):', tphBal.toString());
      console.log('✅ [Swap] TPH Balance (formatted):', tphFormatted);
      console.log('✅ [Swap] USDC Balance (raw):', usdcBal.toString());
      console.log('✅ [Swap] USDC Balance (formatted):', usdcFormatted);

      setTphBalance(tphFormatted);
      setUsdcBalance(usdcFormatted);
    } catch (err) {
      console.error('❌ [Swap] Error fetching balances:', err);
      console.error('❌ [Swap] Error details:', {
        message: err.message,
        code: err.code,
        address,
        tphAddress: TPH_ADDRESS,
        usdcAddress: USDC_ADDRESS
      });
    }
  };

  // Fetch Pool Data
  const fetchPoolData = async (web3Provider) => {
    try {
      const pairContract = new ethers.Contract(PAIR_ADDRESS, PAIR_ABI, web3Provider);
      const [reserve0, reserve1] = await pairContract.getReserves();
      const token0 = await pairContract.token0();

      let tphReserve, usdcReserve;
      if (token0.toLowerCase() === TPH_ADDRESS.toLowerCase()) {
        tphReserve = ethers.formatUnits(reserve0, 18);
        usdcReserve = ethers.formatUnits(reserve1, 6);
      } else {
        usdcReserve = ethers.formatUnits(reserve0, 6);
        tphReserve = ethers.formatUnits(reserve1, 18);
      }

      setPoolData({
        tphReserve,
        usdcReserve,
        loading: false
      });

      // Calculate exchange rate
      if (parseFloat(tphReserve) > 0) {
        const rate = parseFloat(usdcReserve) / parseFloat(tphReserve);
        setExchangeRate(rate.toFixed(8));
      }
    } catch (err) {
      console.error('Error fetching pool data:', err);
      setPoolData(prev => ({ ...prev, loading: false }));
    }
  };

  // Calculate Output Amount using DIRECT PAIR CALCULATION (bypasses router)
  // This matches the working implementation in your DEX dashboard backend
  const calculateOutputAmount = useCallback(async (inputAmount, fromTokenSymbol) => {
    console.log('\n💱 [Swap] ═══ CALCULATION REQUEST (DIRECT PAIR METHOD) ═══');
    console.log('💱 [Swap] Amount:', inputAmount, fromTokenSymbol);
    console.log('💱 [Swap] Current balances - TPH:', tphBalance, 'USDC:', usdcBalance);
    
    if (!inputAmount || parseFloat(inputAmount) <= 0 || !provider) {
      console.log('⚠️ [Swap] Invalid input or no provider - skipping calculation');
      setToAmount('');
      setPriceImpact('0');
      return;
    }

    // Check diagnostic results first
    if (diagnostics.pairExists === false) {
      console.error('❌ [Swap] Cannot calculate: Liquidity pool does not exist');
      setError('⚠️ No liquidity pool exists for TPH/USDC. Please create a pool on the DEX first: https://dex.trendphile.com');
      setToAmount('0');
      return;
    }

    if (diagnostics.hasLiquidity === false) {
      console.error('❌ [Swap] Cannot calculate: Pool has no liquidity');
      setError('⚠️ Liquidity pool exists but is empty. Please add liquidity on the DEX first.');
      setToAmount('0');
      return;
    }

    try {
      console.log('🔍 [Swap] Using DIRECT PAIR calculation (bypassing router)');
      console.log('🔍 [Swap] Pair address:', PAIR_ADDRESS);
      
      // Get reserves directly from pair
      const pairContract = new ethers.Contract(PAIR_ADDRESS, PAIR_ABI, provider);
      const [reserve0, reserve1] = await pairContract.getReserves();
      const token0 = await pairContract.token0();
      
      console.log('🏊 [Swap] Pool reserves:');
      console.log('   ├─ token0:', token0);
      console.log('   ├─ reserve0:', ethers.formatUnits(reserve0, 6), 'USDC');
      console.log('   ├─ reserve1:', ethers.formatUnits(reserve1, 18), 'TPH');
      
      // Determine which reserve is which
      const tphIsToken0 = token0.toLowerCase() === TPH_ADDRESS.toLowerCase();
      const tphReserve = tphIsToken0 ? reserve0 : reserve1;
      const usdcReserve = tphIsToken0 ? reserve1 : reserve0;
      
      console.log('   ├─ TPH reserve:', ethers.formatUnits(tphReserve, 18));
      console.log('   └─ USDC reserve:', ethers.formatUnits(usdcReserve, 6));
      
      let amountOut;
      if (fromTokenSymbol === 'TPH') {
        // TPH → USDC
        const amountIn = ethers.parseUnits(inputAmount, 18);
        // Uniswap V2 constant product formula with 0.3% fee
        const amountInWithFee = amountIn * 997n;
        const numerator = amountInWithFee * usdcReserve;
        const denominator = tphReserve * 1000n + amountInWithFee;
        amountOut = numerator / denominator;
        
        console.log('🧮 [Swap] Manual calculation (TPH → USDC):');
        console.log('   ├─ Amount in:', ethers.formatUnits(amountIn, 18), 'TPH');
        console.log('   └─ Amount out:', ethers.formatUnits(amountOut, 6), 'USDC');
        
        const formattedOutput = parseFloat(ethers.formatUnits(amountOut, 6)).toFixed(6);
        setToAmount(formattedOutput);
        console.log('✅ [Swap] Set toAmount to:', formattedOutput);
      } else {
        // USDC → TPH
        const amountIn = ethers.parseUnits(inputAmount, 6);
        const amountInWithFee = amountIn * 997n;
        const numerator = amountInWithFee * tphReserve;
        const denominator = usdcReserve * 1000n + amountInWithFee;
        amountOut = numerator / denominator;
        
        console.log('🧮 [Swap] Manual calculation (USDC → TPH):');
        console.log('   ├─ Amount in:', ethers.formatUnits(amountIn, 6), 'USDC');
        console.log('   └─ Amount out:', ethers.formatUnits(amountOut, 18), 'TPH');
        
        const formattedOutput = parseFloat(ethers.formatUnits(amountOut, 18)).toFixed(2);
        setToAmount(formattedOutput);
        console.log('✅ [Swap] Set toAmount to:', formattedOutput);
      }
      
      setError(''); // Clear any previous errors

      // Calculate price impact
      const expectedRate = fromTokenSymbol === 'TPH'
        ? parseFloat(exchangeRate)
        : 1 / parseFloat(exchangeRate);
      const actualRate = fromTokenSymbol === 'TPH'
        ? parseFloat(ethers.formatUnits(amountOut, 6)) / parseFloat(inputAmount)
        : parseFloat(ethers.formatUnits(amountOut, 18)) / parseFloat(inputAmount);
      const impact = ((expectedRate - actualRate) / expectedRate) * 100;
      const impactFormatted = Math.abs(impact).toFixed(2);
      setPriceImpact(impactFormatted);
      
      console.log('📊 [Swap] Price impact:', impactFormatted + '%');
      console.log('💱 [Swap] ═══ CALCULATION SUCCESS ═══\n');
    } catch (err) {
      console.error('\n❌ [Swap] ═══ CALCULATION FAILED ═══');
      console.error('❌ [Swap] Error:', err);
      console.error('❌ [Swap] Error details:', {
        message: err.message,
        code: err.code,
        reason: err.reason
      });
      
      let errorMessage = '⚠️ Failed to calculate output. ';
      if (err.code === 'NETWORK_ERROR') {
        errorMessage += 'Network connection issue.';
      } else {
        errorMessage += err.message;
      }
      
      setToAmount('0');
      setError(errorMessage);
      console.error('❌ [Swap] ═══════════════════════════\n');
    }
  }, [provider, exchangeRate, tphBalance, usdcBalance, diagnostics]);

  // Check if approval is needed (not required for backend API swaps)
  const checkApproval = async () => {
    // Backend API handles swaps, no user approval needed
    setNeedsApproval(false);
  };

  // Note: Approval not needed for backend API swaps
  // Backend handles the swap using deployer wallet
  const approveToken = async () => {
    console.log('ℹ️ [Swap] Approval not required - backend handles swaps');
    setError('No approval needed - swaps are executed by backend');
    setTimeout(() => setError(''), 3000);
  };

  // Execute Swap using BACKEND API (matches working DEX dashboard implementation)
  const executeSwap = async () => {
    console.log('🚀 [Swap] Starting swap execution via BACKEND API...');
    console.log('💰 [Swap] User wants to swap:', fromAmount, fromToken);
    console.log('💰 [Swap] User balance:', fromToken === 'TPH' ? tphBalance : usdcBalance);
    
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Validate sufficient balance
    const userBalance = fromToken === 'TPH' ? parseFloat(tphBalance) : parseFloat(usdcBalance);
    if (parseFloat(fromAmount) > userBalance) {
      setError(`Insufficient ${fromToken} balance. You have ${userBalance.toFixed(6)} but trying to swap ${fromAmount}`);
      return;
    }

    setIsSwapping(true);
    setError('');
    setTxHash('');
    setSwapStep('Preparing swap...');

    try {
      console.log('🔍 [Swap] Using BACKEND API for swap execution');
      console.log('🔍 [Swap] API endpoint: https://dex.trendphile.com/api/swap/execute');
      
      const swapDirection = fromToken === 'TPH' ? 'SELL' : 'BUY';
      const payload = {
        amount: parseFloat(fromAmount),
        walletAddress: walletAddress,
        fromToken: fromToken,
        toToken: toToken
      };
      
      console.log('📋 [Swap] Request payload:', payload);
      
      setSwapStep('Executing swap via backend...');
      
      const response = await fetch('https://dex.trendphile.com/api/swap/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('📥 [Swap] Backend response:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Swap failed');
      }

      console.log('✅ [Swap] Swap executed successfully!');
      console.log('  - Transaction hash:', data.txHash);
      console.log('  - Amount swapped:', data.amountIn, fromToken);
      console.log('  - Amount received:', data.amountOut, toToken);
      
      setTxHash(data.txHash);
      setSwapStep('Confirming transaction...');

      // Wait a bit for transaction to be mined
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setSuccess(`Swap successful! Swapped ${fromAmount} ${fromToken} for ~${toAmount} ${toToken}`);
      setFromAmount('');
      setToAmount('');
      
      // Refresh balances
      console.log('🔄 [Swap] Refreshing balances...');
      await fetchBalances(walletAddress, provider);
      await fetchPoolData(provider);
      
    } catch (err) {
      console.error('❌ [Swap] Swap execution error:', err);
      console.error('❌ [Swap] Error details:', {
        message: err.message,
        status: err.status
      });
      
      // Better error messages
      let errorMessage = 'Swap failed: ';
      
      if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Backend API unavailable. Please try again later.';
      } else if (err.message.includes('insufficient')) {
        errorMessage = err.message;
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSwapping(false);
      setSwapStep('');
    }
  };

  // Switch token direction
  const switchTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  // Set max balance
  const setMaxAmount = () => {
    const balance = fromToken === 'TPH' ? tphBalance : usdcBalance;
    setFromAmount(balance);
  };

  // Handle from amount change with debounce
  useEffect(() => {
    // Debounce the calculation to avoid too many API calls
    const timeoutId = setTimeout(() => {
      if (fromAmount && parseFloat(fromAmount) > 0) {
        console.log('⏱️ [Swap] Debounced calculation triggered for:', fromAmount);
        calculateOutputAmount(fromAmount, fromToken);
        checkApproval();
      } else {
        setToAmount('');
        setNeedsApproval(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [fromAmount, fromToken, calculateOutputAmount]);

  // Auto-detect wallet on component mount with retry logic
  useEffect(() => {
    console.log('🚀 [Swap] Component mounted, initializing wallet detection...');
    
    // Wait a bit for MetaMask to fully load, then check connection
    const initWallet = async () => {
      // First attempt immediately
      await checkExistingConnection();
      
      // If still not connected after 1 second, try again (MetaMask might be slow to load)
      setTimeout(async () => {
        if (!isConnected && isMetaMaskInstalled()) {
          console.log('🔄 [Swap] Retrying wallet detection after delay...');
          await checkExistingConnection();
        }
      }, 1000);
    };
    
    initWallet();
  }, []);

  // Listen to account/network changes
  useEffect(() => {
    console.log('👂 [Swap] Setting up event listeners...');
    
    if (isMetaMaskInstalled()) {
      const handleAccountsChanged = (accounts) => {
        console.log('🔄 [Swap] Accounts changed:', accounts);
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
          fetchBalances(accounts[0], provider);
          setError(''); // Clear error when wallet is connected
        } else {
          setIsConnected(false);
          setWalletAddress('');
          setError('Please connect your wallet from the My Account page (TPH tab) first');
        }
      };

      const handleChainChanged = (chainId) => {
        console.log('🔗 [Swap] Chain changed:', chainId);
        window.location.reload();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      return () => {
        console.log('🧹 [Swap] Cleaning up event listeners...');
        if (window.ethereum?.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, [provider]);

  // Get button text
  const getButtonText = () => {
    if (!isConnected) return 'Wallet Not Connected';
    if (isWrongNetwork) return 'Switch to Polygon Mainnet';
    if (!fromAmount || parseFloat(fromAmount) <= 0) return 'Enter Amount';
    
    const balance = fromToken === 'TPH' ? tphBalance : usdcBalance;
    if (parseFloat(fromAmount) > parseFloat(balance)) return 'Insufficient Balance';
    
    if (needsApproval) return `Approve ${fromToken}`;
    return `Swap ${fromToken} for ${toToken}`;
  };

  // Get button handler
  const handleButtonClick = () => {
    if (!isConnected) return; // Do nothing - wallet must be connected from My Account page
    if (isWrongNetwork) return switchNetwork();
    if (needsApproval) return approveToken();
    return executeSwap();
  };

  // Is button disabled
  const isButtonDisabled = () => {
    if (!isConnected) return true; // Disabled if wallet not connected
    if (isWrongNetwork) return false; // Can click to switch network
    if (!fromAmount || parseFloat(fromAmount) <= 0) return true;
    
    const balance = fromToken === 'TPH' ? tphBalance : usdcBalance;
    if (parseFloat(fromAmount) > parseFloat(balance)) return true;
    
    return isSwapping || isApproving;
  };

  // Show golden crescent moon loading during initialization
  if (isInitializing) {
    return (
      <div className="loading-overlay">
        <div className="loading-moon-wrapper">
          <div className="loading-moon-crescent">
            <Moon size={80} strokeWidth={2} />
          </div>
          <div className="loading-moon-glow"></div>
          <div className="loading-moon-particles">
            <span className="loading-particle"></span>
            <span className="loading-particle"></span>
            <span className="loading-particle"></span>
          </div>
        </div>
        <p className="loading-text">Initializing Swap...</p>
      </div>
    );
  }

  return (
    <div className="swap-container">
      <div className="swap-wrapper">
        {/* Header */}
        <div className="swap-header">
          <div className="swap-title">
            <TrendingUp className="title-icon" />
            <h1>Token Swap</h1>
          </div>
          <p className="swap-subtitle">
            Convert between TPH and USDC instantly
          </p>
        </div>

        {/* Main Swap Card */}
        <div className="swap-card">
          {/* Wallet Info */}
          {isConnected && (
            <div className="wallet-info">
              <div className="wallet-address">
                <Wallet size={16} />
                <span>{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
              </div>
              <button 
                className="settings-btn"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings size={18} />
              </button>
            </div>
          )}

          {/* Settings Panel */}
          {showSettings && (
            <div className="settings-panel">
              <div className="settings-header">
                <h3>Transaction Settings</h3>
                <button onClick={() => setShowSettings(false)}>
                  <X size={18} />
                </button>
              </div>
              
              <div className="setting-group">
                <label>Slippage Tolerance (%)</label>
                <div className="slippage-options">
                  <button 
                    className={slippage === '0.5' ? 'active' : ''}
                    onClick={() => setSlippage('0.5')}
                  >
                    0.5%
                  </button>
                  <button 
                    className={slippage === '1' ? 'active' : ''}
                    onClick={() => setSlippage('1')}
                  >
                    1%
                  </button>
                  <button 
                    className={slippage === '2' ? 'active' : ''}
                    onClick={() => setSlippage('2')}
                  >
                    2%
                  </button>
                  <input 
                    type="number"
                    value={slippage}
                    onChange={(e) => setSlippage(e.target.value)}
                    placeholder="Custom"
                  />
                </div>
              </div>

              <div className="setting-group">
                <label>Transaction Deadline (minutes)</label>
                <input 
                  type="number"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  min="1"
                  max="60"
                />
              </div>
            </div>
          )}

          {/* From Token Input */}
          <div className="token-input-container">
            <div className="input-header">
              <span className="label">From</span>
              <span className="balance">
                Balance: {fromToken === 'TPH' ? parseFloat(tphBalance).toFixed(2) : parseFloat(usdcBalance).toFixed(2)}
              </span>
            </div>
            
            <div className="input-wrapper">
              <button 
                className="max-btn"
                onClick={setMaxAmount}
                disabled={!isConnected || isWrongNetwork}
              >
                MAX
              </button>
              
              <input 
                type="number"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                placeholder="0.0"
                disabled={!isConnected || isWrongNetwork}
              />
              
              <div className="token-selector">
                <div className="token-info">
                  <DollarSign size={20} className="token-icon" />
                  <span className="token-symbol">{fromToken}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Switch Button */}
          <div className="switch-wrapper">
            <button 
              className="switch-btn"
              onClick={switchTokens}
              disabled={!isConnected || isWrongNetwork}
            >
              <ArrowDownUp size={20} />
            </button>
          </div>

          {/* To Token Input */}
          <div className="token-input-container">
            <div className="input-header">
              <span className="label">To</span>
              <span className="balance">
                Balance: {toToken === 'TPH' ? parseFloat(tphBalance).toFixed(2) : parseFloat(usdcBalance).toFixed(2)}
              </span>
            </div>
            
            <div className="input-wrapper">
              <input 
                type="number"
                value={toAmount}
                readOnly
                placeholder="0.0"
              />
              
              <div className="token-selector">
                <div className="token-info">
                  <DollarSign size={20} className="token-icon" />
                  <span className="token-symbol">{toToken}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Swap Details */}
          {fromAmount && parseFloat(fromAmount) > 0 && (
            <div className="swap-details">
              <div className="detail-row">
                <span>Exchange Rate</span>
                <span>1 {fromToken} = {fromToken === 'TPH' ? exchangeRate : (1 / parseFloat(exchangeRate)).toFixed(8)} {toToken}</span>
              </div>
              <div className="detail-row">
                <span>Price Impact</span>
                <span className={parseFloat(priceImpact) > 5 ? 'warning' : ''}>
                  {priceImpact}%
                </span>
              </div>
              <div className="detail-row">
                <span>Minimum Received</span>
                <span>{(parseFloat(toAmount) * (1 - parseFloat(slippage) / 100)).toFixed(toToken === 'TPH' ? 2 : 6)} {toToken}</span>
              </div>
              <div className="detail-row">
                <span>Slippage Tolerance</span>
                <span>{slippage}%</span>
              </div>
            </div>
          )}

          {/* Pool Info */}
          {!poolData.loading && (
            <div className="pool-info">
              <Info size={14} />
              <span>
                Pool: {parseFloat(poolData.tphReserve).toLocaleString()} TPH / {parseFloat(poolData.usdcReserve).toFixed(2)} USDC
              </span>
            </div>
          )}

          {/* Error Message / Wallet Not Connected Warning */}
          {error && (
            <div className="message error-message">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Additional help for non-connected wallets */}
          {!isConnected && !error && (
            <div className="message error-message">
              <Info size={16} />
              <div>
                <strong>Wallet Not Connected</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>
                  Please go to <a href="/login?tab=tphile" style={{ color: '#FFD700', textDecoration: 'underline' }}>My Account → TPH tab</a> to connect your wallet first.
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="message success-message">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}

          {/* Transaction Hash */}
          {txHash && (
            <div className="tx-hash">
              <span>Transaction:</span>
              <a 
                href={`${EXPLORER_URL}/tx/${txHash}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {txHash.slice(0, 10)}...{txHash.slice(-8)}
                <ExternalLink size={14} />
              </a>
            </div>
          )}

          {/* Swap Step */}
          {swapStep && (
            <div className="swap-step">
              <Loader className="spinning" size={16} />
              <span>{swapStep}</span>
            </div>
          )}

          {/* Swap Button */}
          <button 
            className={`swap-btn ${needsApproval ? 'approve-btn' : ''} ${isButtonDisabled() ? 'disabled' : ''}`}
            onClick={handleButtonClick}
            disabled={isButtonDisabled()}
          >
            {(isSwapping || isApproving) && <Loader className="spinning" size={18} />}
            {getButtonText()}
          </button>

          {/* Warning for high price impact */}
          {parseFloat(priceImpact) > 5 && fromAmount && (
            <div className="high-impact-warning">
              <AlertTriangle size={16} />
              <span>High price impact! Consider reducing your swap amount.</span>
            </div>
          )}
        </div>

        {/* Additional Info Cards */}
        <div className="info-cards">
          <div className="info-card">
            <Zap className="card-icon" />
            <h3>Instant Swaps</h3>
            <p>Trade tokens instantly with no waiting period</p>
          </div>
          
          <div className="info-card">
            <Shield className="card-icon" />
            <h3>Secure</h3>
            <p>Your tokens never leave your wallet until you confirm</p>
          </div>
          
          <div className="info-card">
            <TrendingUp className="card-icon" />
            <h3>Best Rates</h3>
            <p>Get competitive rates directly from the liquidity pool</p>
          </div>
        </div>

        {/* Back to Main Site Link */}
        <div className="back-link">
          <a href="https://trendphile.com">
            ← Back to Trendphile
          </a>
        </div>
      </div>
    </div>
  );
};

export default Swap;

